import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, Clock, User, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import KioskLayout from "@/components/KioskLayout";
import { Button } from "@/components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RETURN_PROCEDURE_IDS = [6, 82];

export default function KioskAppointments() {
  const navigate = useNavigate();
  const { action } = useParams();

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [patient, setPatient] = useState(null);

  const [checkinStep, setCheckinStep] = useState("list");
  const [processingCheckin, setProcessingCheckin] = useState(false);
  const [finalMessage, setFinalMessage] = useState("");

  useEffect(() => {
    const storedPatient = sessionStorage.getItem("patient");

    if (!storedPatient) {
      toast.error("Sessão expirada. Por favor, identifique-se novamente.");
      navigate(`/identify/${action}`);
      return;
    }

    const patientData = JSON.parse(storedPatient);
    setPatient(patientData);
    fetchAppointments(patientData.id || patientData.paciente_id);
  }, [action, navigate]);

  const fetchAppointments = async (patientId) => {
    try {
      const endpoint =
        action === "checkin"
          ? `${API_URL}/api/patients/${patientId}/appointments/today`
          : `${API_URL}/api/patients/${patientId}/appointments/all`;

      const response = await axios.get(endpoint);

      if (response.data.success) {
        setAppointments(response.data.appointments || []);

        if ((response.data.appointments || []).length === 0) {
          toast.info(
            action === "checkin"
              ? "Você não tem consultas agendadas para hoje."
              : "Você não tem consultas para remarcar."
          );
        }
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
      toast.error("Erro ao buscar agendamentos. Por favor, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const getAppointmentId = (appointment) =>
    appointment?.appointment_id ||
    appointment?.agendamento_id ||
    appointment?.id;

  const getProfessionalName = (appointment) =>
    appointment?.professional_name ||
    appointment?.profissional ||
    appointment?.professional ||
    "Profissional";

  const getSpecialtyName = (appointment) =>
    appointment?.specialty_name ||
    appointment?.especialidade ||
    appointment?.specialty ||
    "Consulta";

  const getDateValue = (appointment) =>
    appointment?.date || appointment?.data || "";

  const getTimeValue = (appointment) =>
    appointment?.time || appointment?.hora || appointment?.horario || "";

  const getProcedureId = (appointment) =>
    appointment?.procedure_id ||
    appointment?.procedimento_id ||
    appointment?.raw?.procedure_id ||
    appointment?.raw?.procedimento_id;

  const isReturnAppointment = (appointment) => {
    const procedureId = Number(getProcedureId(appointment));
    return RETURN_PROCEDURE_IDS.includes(procedureId);
  };

  const isValidId = (value) => {
    if (value === null || value === undefined || value === "") return false;
    return Number(value) > 0;
  };

  const getPatientInsuranceId = () =>
    patient?.convenio_id ||
    patient?.convenios?.[0]?.convenio_id ||
    patient?.convenio?.id ||
    patient?.convenio?.convenio_id ||
    patient?.insurance_id ||
    patient?.raw?.convenio_id ||
    patient?.raw?.convenios?.[0]?.convenio_id ||
    patient?.raw?.convenio?.id ||
    patient?.raw?.convenio?.convenio_id ||
    null;

  const getPatientInsurancePlanId = () =>
    patient?.convenio_plano_id ??
    patient?.convenios?.[0]?.plano_id ??
    patient?.convenio?.plano_id ??
    patient?.plano_id ??
    patient?.insurance_plan_id ??
    patient?.raw?.convenio_plano_id ??
    patient?.raw?.convenios?.[0]?.plano_id ??
    patient?.raw?.convenio?.plano_id ??
    null;

  const getAppointmentInsuranceId = (appointment) =>
    appointment?.convenio_id ||
    appointment?.insurance_id ||
    appointment?.raw?.convenio_id ||
    appointment?.raw?.insurance_id ||
    null;

  const getAppointmentInsurancePlanId = (appointment) =>
    appointment?.convenio_plano_id ??
    appointment?.plano_id ??
    appointment?.insurance_plan_id ??
    appointment?.raw?.convenio_plano_id ??
    appointment?.raw?.plano_id ??
    appointment?.raw?.insurance_plan_id ??
    null;

  const getCheckinType = (appointment) => {
    if (isReturnAppointment(appointment)) return "RETORNO";

    const insuranceId =
      getAppointmentInsuranceId(appointment) || getPatientInsuranceId();

    const planId =
      getAppointmentInsurancePlanId(appointment) || getPatientInsurancePlanId();

    if (isValidId(insuranceId) || isValidId(planId)) {
      return "CONVENIO";
    }

    return "PARTICULAR";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    const parts = dateStr.split("-");

    if (parts.length === 3) {
      return `${parts[0]}/${parts[1]}/${parts[2]}`;
    }

    return dateStr;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    return timeStr.slice(0, 5);
  };

  const handleContinue = () => {
    if (!selectedAppointment) {
      toast.error("Selecione uma consulta para continuar.");
      return;
    }

    sessionStorage.setItem(
      "selectedAppointment",
      JSON.stringify(selectedAppointment)
    );

    const selectedId = getAppointmentId(selectedAppointment);

    if (action === "checkin") {
      const tipoConsulta = getCheckinType(selectedAppointment);
      handleCheckinByType(tipoConsulta);
      return;
    }

    navigate(`/reschedule/${selectedId}`);
  };

  const handleCheckinByType = async (tipoConsulta) => {
    if (!selectedAppointment || !patient) {
      toast.error("Dados da consulta não encontrados. Por favor, tente novamente.");
      return;
    }

    const appointmentId = getAppointmentId(selectedAppointment);

    const patientId =
      selectedAppointment.patient_id ||
      selectedAppointment.paciente_id ||
      patient.id ||
      patient.paciente_id;

    if (!appointmentId || !patientId) {
      toast.error("Não foi possível identificar a consulta selecionada.");
      return;
    }

    setProcessingCheckin(true);

    try {
      const response = await axios.post(`${API_URL}/api/checkin`, {
        appointment_id: Number(appointmentId),
        patient_id: Number(patientId),
        tipo_consulta: tipoConsulta,
      });

      setFinalMessage(
        response.data?.message ||
          "Presença registrada. Por favor, dirija-se à recepção."
      );

      setCheckinStep("success");

      if (response.data?.success) {
        setTimeout(() => {
          sessionStorage.removeItem("selectedAppointment");
          navigate("/");
        }, 15000);
      }
    } catch (error) {
      console.error("Error performing checkin:", error);

      setFinalMessage(
        error.response?.data?.detail ||
          "Não foi possível registrar sua presença. Por favor, dirija-se à recepção."
      );

      setCheckinStep("success");
    } finally {
      setProcessingCheckin(false);
    }
  };

  if (loading) {
    return (
      <KioskLayout showBack step="Agendamentos">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="spinner mx-auto mb-4" />
            <p className="text-2xl text-slate-500">
              Buscando seus agendamentos...
            </p>
          </div>
        </div>
      </KioskLayout>
    );
  }

  if (action === "checkin" && checkinStep === "success") {
    return (
      <KioskLayout step="Confirmação">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-3xl mx-auto text-center">
            <CheckCircle className="w-28 h-28 text-green-500 mx-auto mb-8" />

            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6 font-['Outfit'] tracking-tight">
              Presença registrada
            </h2>

            <p className="text-2xl md:text-3xl text-slate-600 leading-relaxed mb-10">
              {finalMessage}
            </p>

            <Button
              onClick={() => navigate("/")}
              className="kiosk-primary-btn px-8 py-4 text-xl w-auto min-w-[260px]"
            >
              Voltar ao Início
            </Button>
          </div>
        </div>
      </KioskLayout>
    );
  }

  return (
    <KioskLayout showBack step="Agendamentos">
      <div className="flex-1 flex flex-col p-8">
        <div className="w-full max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4 font-['Outfit'] tracking-tight">
              {action === "checkin"
                ? "Suas Consultas de Hoje"
                : "Selecione a Consulta"}
            </h2>

            {patient && (
              <p className="text-xl md:text-2xl text-slate-500">
                Olá,{" "}
                <span className="font-semibold text-[#00357C]">
                  {patient.nome || patient.name}
                </span>
              </p>
            )}
          </div>

          {appointments.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="w-24 h-24 text-slate-300 mx-auto mb-6" />

              <p className="text-2xl text-slate-500 mb-6">
                {action === "checkin"
                  ? "Nenhuma consulta encontrada para hoje."
                  : "Nenhuma consulta disponível para remarcação."}
              </p>

              <div className="flex flex-col md:flex-row items-center justify-center gap-4 mt-6">
                {action === "reschedule" && (
                  <Button
                    onClick={() => {
                      sessionStorage.removeItem("selectedAppointment");
                      navigate("/reschedule/new");
                    }}
                    className="kiosk-primary-btn px-6 py-3 text-lg w-auto min-w-[260px]"
                  >
                    Marcar Nova Consulta
                  </Button>
                )}

                <Button
                  data-testid="back-home-btn"
                  onClick={() => navigate("/")}
                  className="kiosk-secondary-btn px-6 py-3 text-lg w-auto min-w-[220px]"
                >
                  Voltar ao Início
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4 mb-8">
                {appointments.map((appointment, index) => {
                  const currentId = getAppointmentId(appointment);
                  const selectedId = getAppointmentId(selectedAppointment);
                  const retorno = isReturnAppointment(appointment);

                  return (
                    <button
                      key={currentId || index}
                      data-testid={`appointment-card-${index}`}
                      onClick={() => setSelectedAppointment(appointment)}
                      className={`appointment-card w-full text-left ${
                        selectedId === currentId ? "selected" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="text-2xl font-semibold text-slate-800">
                              {getSpecialtyName(appointment)}
                            </h3>

                            {retorno && (
                              <span className="px-4 py-1 rounded-full bg-green-100 text-green-700 text-base font-semibold">
                                Retorno
                              </span>
                            )}
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center gap-3 text-xl text-slate-600">
                              <User className="w-6 h-6 text-slate-400" />
                              <span>{getProfessionalName(appointment)}</span>
                            </div>

                            <div className="flex items-center gap-3 text-xl text-slate-600">
                              <Calendar className="w-6 h-6 text-slate-400" />
                              <span>{formatDate(getDateValue(appointment))}</span>
                            </div>

                            <div className="flex items-center gap-3 text-xl text-slate-600">
                              <Clock className="w-6 h-6 text-slate-400" />
                              <span>{formatTime(getTimeValue(appointment))}</span>
                            </div>
                          </div>
                        </div>

                        <div
                          className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                            selectedId === currentId
                              ? "border-[#00357C] bg-[#00357C]"
                              : "border-slate-300"
                          }`}
                        >
                          {selectedId === currentId && (
                            <div className="w-3 h-3 rounded-full bg-white" />
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-col gap-4">
                <Button
                  data-testid="continue-btn"
                  onClick={handleContinue}
                  disabled={!selectedAppointment || processingCheckin}
                  className="kiosk-primary-btn w-full"
                >
                  {processingCheckin
                    ? "Registrando presença..."
                    : action === "checkin"
                    ? "Confirmar Presença"
                    : "Remarcar Esta Consulta"}
                </Button>

                {action === "reschedule" && (
                  <Button
                    onClick={() => {
                      sessionStorage.removeItem("selectedAppointment");
                      navigate("/reschedule/new");
                    }}
                    className="kiosk-secondary-btn w-full"
                  >
                    Marcar Nova Consulta
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </KioskLayout>
  );
}